import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Clock, Sparkles } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { listArticles, listArticleCategories, getArticle } from "../lib/api";

function CategoryPill({ active, label, count, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`library-cat-${label.toLowerCase()}`}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-card border border-border text-foreground/70 hover:border-primary/40 hover:text-foreground"}`}
    >
      {label} <span className="opacity-60 font-bold">{count}</span>
    </button>
  );
}

export default function LibraryPage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [reading, setReading] = useState(null);

  useEffect(() => {
    listArticles(cat).then(setArticles).catch(() => {});
  }, [cat]);
  useEffect(() => {
    listArticleCategories().then(setCats).catch(() => {});
  }, []);
  useEffect(() => {
    if (articleId) getArticle(articleId).then(setReading).catch(() => {});
    else setReading(null);
  }, [articleId]);

  if (reading) {
    return (
      <div className="kanya-page-pad-bottom" data-testid="article-page">
        <div className="px-4 pt-4">
          <button onClick={() => navigate("/library")} className="inline-flex items-center gap-1.5 text-[12px] font-bold text-muted-foreground hover:text-foreground transition" data-testid="article-back">
            <ArrowLeft size={14} /> Library
          </button>
        </div>
        <article className="px-5 pt-3">
          <p className="text-[10px] uppercase tracking-wider text-primary font-extrabold">{reading.category}</p>
          <h1 className="mt-2 text-[32px] leading-[1.04] font-extrabold tracking-tight" data-testid="article-title">{reading.title}</h1>
          <p className="mt-2 text-[12px] text-muted-foreground font-bold uppercase tracking-wider flex items-center gap-1.5">
            <Clock size={11} /> {reading.minutes} min read
          </p>
          <p className="mt-5 text-[16px] leading-[1.65] text-foreground/85 whitespace-pre-line font-medium" data-testid="article-body">
            {reading.body}
          </p>
          <div className="mt-8 rounded-[20px] bg-blush p-4 flex items-start gap-3">
            <Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
            <p className="text-[12.5px] text-foreground/85 leading-relaxed font-medium">
              Articles in Knya are general wellness reflections — always consult a clinician for medical advice.
            </p>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="kanya-page-pad-bottom" data-testid="library-page">
      <PageHeader
        eyebrow="Library"
        title="Read with Knya"
        subtitle="Quiet, evidence-aware reads on your body, mind, and rhythm."
      />

      <div className="mt-4 px-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="library-categories">
        <CategoryPill active={!cat} label="All" count={articles.length || ""} onClick={() => setCat("")} />
        {cats.map((c) => (
          <CategoryPill key={c.name} active={cat === c.name} label={c.name} count={c.count} onClick={() => setCat(c.name)} />
        ))}
      </div>

      <div className="px-4 mt-3 space-y-2" data-testid="library-list">
        {articles.length === 0 && (
          <div className="rounded-2xl border border-border bg-muted/30 p-6 text-center">
            <BookOpen size={20} className="mx-auto text-muted-foreground" />
            <p className="text-[13px] text-muted-foreground mt-2 font-medium">No articles in this category yet.</p>
          </div>
        )}
        {articles.map((a, i) => (
          <motion.div
            key={a.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
          >
            <Link
              to={`/library/${a.id}`}
              data-testid={`article-card-${a.id}`}
              className="block rounded-[20px] bg-card border border-border p-4 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-wider text-primary font-extrabold">{a.category}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-extrabold flex items-center gap-1">
                  <Clock size={10} /> {a.minutes} min
                </p>
              </div>
              <h3 className="mt-2 text-[18px] leading-tight font-extrabold tracking-tight">{a.title}</h3>
              <p className="mt-1.5 text-[13px] text-muted-foreground font-medium leading-relaxed">{a.excerpt}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
